/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { DocManifestType, useDocLoader } from '@/pages/DocsPage/useDocLoader';
import { Button } from 'primereact/button';
import { AppRoutes } from '@/constants/routes.ts';
import { classNames } from 'primereact/utils';
import { CNavLink } from '@/components';
import { cleanedPath } from '@/pages/DocsPage/shared-utils.ts';
import { useStoreContext } from '@/contexts/StoreContext';

interface Props {
  className?: string;
  resetSearchResult: () => void;
}

export const DocBottomNavigation: React.FC<Props> = ({ className, resetSearchResult }) => {
  const { manifest } = useDocLoader();
  const { pathname } = useLocation();
  const { externalServiceConfig } = useStoreContext();

  const [nextPage, setNextPage] = useState<DocManifestType | null>(null);
  const [prevPage, setPrevPage] = useState<DocManifestType | null>(null);

  useEffect(() => {
    const currentPageIndex = manifest.findIndex((el) => cleanedPath(pathname) === el.slug);

    if (currentPageIndex === -1) {
      setPrevPage(null);
      setNextPage(null);
      return;
    }

    const orderSortedPages = [...manifest]
      .filter((el) => {
        return externalServiceConfig?.spaces?.serviceUp || el.feature !== 'spaces';
      })
      .sort((a, b) => {
        if (a.order === b.order) {
          const aRank = a.rank || Number.POSITIVE_INFINITY;
          const bRank = b.rank || Number.POSITIVE_INFINITY;
          return aRank - bRank;
        }
        return a.order - b.order;
      });

    const sortedIndex = orderSortedPages.findIndex((el) => cleanedPath(pathname) === el.slug);

    if (sortedIndex > 0) {
      setPrevPage(orderSortedPages[sortedIndex - 1]);
    } else {
      setPrevPage(null);
    }

    if (sortedIndex < orderSortedPages.length - 1) {
      setNextPage(orderSortedPages[sortedIndex + 1]);
    } else {
      setNextPage(null);
    }
  }, [pathname, manifest, externalServiceConfig]);

  const actionLayoutAlignement = (prevPage: boolean, nexPage: boolean) => {
    if (prevPage && nexPage) {
      return 'justify-content-between';
    } else if (prevPage) {
      return 'justify-content-start';
    } else if (nexPage) {
      return 'justify-content-end';
    }
  };

  return (
    <div className={classNames('flex w-full', className, actionLayoutAlignement(!!prevPage, !!nextPage))}>
      {prevPage && (
        <CNavLink onClick={resetSearchResult} to={`${AppRoutes.docs.path}/${prevPage.slug}`}>
          <Button
            icon="pi pi-arrow-left"
            label={prevPage.title}
            outlined
            className="capitalize"
            size="small"
            severity="contrast"
          />
        </CNavLink>
      )}
      {nextPage && (
        <CNavLink onClick={resetSearchResult} to={`${AppRoutes.docs.path}/${nextPage.slug}`}>
          <Button
            icon="pi pi-arrow-right"
            className="capitalize"
            iconPos="right"
            size="small"
            outlined
            severity="contrast"
            label={nextPage.title}
          />
        </CNavLink>
      )}
    </div>
  );
};
